import { readVideoSources, videoMp4Fallback } from '../../common/video-sources/video-sources.js';
import { createViewportObserver } from '../../common/layout-observer/layout-observer.js';

const controllers = new WeakMap();

function initializeHeroVideos(hero) {
    if (controllers.has(hero)) return;
    const document = hero.ownerDocument;
    const view = document.defaultView;
    const track = hero.querySelector('[data-home-hero-slides]');
    const slides = [...(track?.children || [])];
    const states = slides
        .map((slide) => ({ slide, video: slide.querySelector('[data-hero-video]') }))
        .filter(({ video }) => video)
        .map((state) => ({
            ...state,
            source: '',
            failed: '',
            pending: false,
            generation: 0,
            blockedSources: new Set(),
            retried: false,
            fallback: '',
        }));
    if (!view || !track || !states.length) return;

    const mobile = view.matchMedia('(max-width: 47.999rem)');
    const tablet = view.matchMedia('(min-width: 48rem) and (max-width: 63.999rem)');
    const reducedMotion = view.matchMedia('(prefers-reduced-motion: reduce)');
    // The preview pauses carousel advancement before initializing this component.
    // Retain that host setting while still previewing videos.
    const hostPaused = track.dataset.blazeAutoplayPaused === 'true';
    let activeSlide = slides.find((slide) => slide.dataset.heroActive === 'true') || slides[0];
    let visible = false;
    let disposed = false;

    const paused = () => reducedMotion.matches;
    const deviceFor = () => (mobile.matches ? 'mobile' : tablet.matches ? 'tablet' : 'desktop');
    const sourceFor = (state) => {
        const source =
            (mobile.matches
                ? state.video.dataset.srcMobile
                : tablet.matches
                  ? state.video.dataset.srcTablet
                  : '') ||
            state.video.dataset.src ||
            '';
        return source ? `${deviceFor()}:${source}` : '';
    };
    const attachSources = (video) => {
        const device = deviceFor();
        video.querySelectorAll('source[data-hero-video-src]').forEach((source) => {
            if (source.dataset.heroVideoDevice === device) source.src = source.dataset.heroVideoSrc;
            else source.removeAttribute('src');
        });
    };
    const isVisible = () => {
        if (!visible || document.hidden || !hero.isConnected) return false;
        if (observer) return true;
        // The compatibility fallback has no native viewport snapshots.
        const rect = hero.getBoundingClientRect();
        return (
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < (document.documentElement.clientHeight || view.innerHeight) &&
            rect.left < (document.documentElement.clientWidth || view.innerWidth) &&
            view.getComputedStyle(hero).visibility === 'visible'
        );
    };
    const canPlay = (state) => !disposed && !paused() && state.slide === activeSlide && isVisible();
    const pause = (state) => {
        state.generation += 1;
        state.pending = false;
        state.video.pause();
    };
    const reset = (state) => {
        pause(state);
        delete state.video.dataset.heroVideoPlaying;
        state.video.removeAttribute('src');
        state.video
            .querySelectorAll('source[src]')
            .forEach((source) => source.removeAttribute('src'));
        state.video.load();
        state.source = '';
        state.failed = '';
        state.retried = false;
        state.fallback = '';
    };
    const play = (state, source) => {
        const { video } = state;
        if (
            !source ||
            state.failed === source ||
            state.blockedSources.has(source) ||
            state.pending ||
            !video.paused
        )
            return;
        if (state.source !== source) {
            // Only the active, visible device source is ever attached. Other
            // slides and responsive variants retain their inert data attributes.
            state.source = source;
            attachSources(video);
            video.load();
        }
        if (state.fallback && video.src !== state.fallback) {
            video.src = state.fallback;
            video.load();
        }
        const generation = ++state.generation;
        state.pending = true;
        const rejected = (error) => {
            if (generation !== state.generation) return;
            state.pending = false;
            // Retain the responsive poster when autoplay is denied. Retrying
            // needs a real interaction; never change the configured sound.
            if (error?.name === 'NotAllowedError') state.blockedSources.add(source);
        };
        try {
            Promise.resolve(video.play())
                .then(() => {
                    if (generation !== state.generation) {
                        if (!canPlay(state)) pause(state);
                        return;
                    }
                    state.pending = false;
                    if (!canPlay(state)) pause(state);
                    else if (!video.paused) video.dataset.heroVideoPlaying = 'true';
                })
                .catch(rejected);
        } catch (error) {
            rejected(error);
        }
    };
    const refresh = () => {
        if (disposed) return;
        if (!hero.isConnected) {
            disconnect();
            return;
        }
        states.forEach((state) => {
            const source = sourceFor(state);
            if (state.source && state.source !== source) reset(state);
            if (canPlay(state)) play(state, source);
            else pause(state);
        });
    };
    const syncMotion = () => {
        const motionPaused = paused();
        track.dataset.blazeAutoplayPaused = String(hostPaused || motionPaused);
        track.dispatchEvent(
            new view.CustomEvent('zirbelino:slider-autoplay-change', {
                bubbles: true,
                detail: { paused: hostPaused || motionPaused },
            }),
        );
        refresh();
    };
    const onMotionChange = syncMotion;
    const onInteraction = (event) => {
        if (!event.isTrusted) return;
        if (
            event.type === 'keydown' &&
            (event.ctrlKey ||
                event.altKey ||
                event.metaKey ||
                ['Escape', 'Shift', 'Control', 'Alt', 'Meta'].includes(event.key))
        )
            return;
        states.forEach((state) => {
            const source = sourceFor(state);
            if (!state.blockedSources.has(source) || !canPlay(state)) return;
            state.blockedSources.delete(source);
            // Keep play() synchronous inside the trusted click/keydown so
            // browser user activation remains available on touch devices too.
            play(state, source);
        });
    };
    const onPrepare = (event) => {
        if (!slides.includes(event.detail?.slide)) return;
        activeSlide = event.detail.slide;
        refresh();
    };
    const onSlideChange = (event) => {
        const index = Number(event.detail?.firstSlideIndex);
        const slide = slides.find((slide) => Number(slide.dataset.heroSlideIndex) === index);
        if (!slide) return;
        activeSlide = slide;
        refresh();
    };
    const observer =
        typeof view.IntersectionObserver === 'function'
            ? createViewportObserver((entries) => {
                  const nextVisible = entries.some((entry) => entry.visible);
                  return () => {
                      visible = nextVisible;
                      refresh();
                  };
              }, view)
            : null;
    const removalObserver =
        typeof view.MutationObserver === 'function'
            ? new view.MutationObserver(() => {
                  if (!hero.isConnected) disconnect();
              })
            : null;
    function disconnect() {
        if (disposed) return;
        disposed = true;
        observer?.disconnect();
        removalObserver?.disconnect();
        states.forEach((state) => {
            pause(state);
            state.video.removeEventListener('playing', state.onPlaying);
            state.video.removeEventListener('error', state.onError);
            if (state.source) {
                state.video.removeAttribute('src');
                state.video
                    .querySelectorAll('source[src]')
                    .forEach((source) => source.removeAttribute('src'));
                state.video.load();
            }
        });
        document.removeEventListener('visibilitychange', refresh);
        document.removeEventListener('click', onInteraction, { capture: true });
        document.removeEventListener('keydown', onInteraction, { capture: true });
        mobile.removeEventListener('change', refresh);
        tablet.removeEventListener('change', refresh);
        reducedMotion.removeEventListener('change', onMotionChange);
        track.removeEventListener('zirbelino:slider-prepare', onPrepare);
        track.removeEventListener('zirbelino:slider-change', onSlideChange);
        controllers.delete(hero);
    }

    states.forEach((state) => {
        const { video } = state;
        video.controls = false;
        video.autoplay = true;
        const muted = video.dataset.heroVideoMuted !== 'false';
        video.muted = muted;
        video.defaultMuted = muted;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';
        state.onPlaying = () => {
            if (canPlay(state)) video.dataset.heroVideoPlaying = 'true';
            else pause(state);
        };
        state.onError = () => {
            if (!state.source) return;
            const fallback = state.retried
                ? ''
                : videoMp4Fallback(video.currentSrc, readVideoSources(video));
            state.failed = fallback ? '' : state.source;
            delete video.dataset.heroVideoPlaying;
            pause(state);
            if (fallback) {
                state.retried = true;
                state.fallback = fallback;
                if (canPlay(state)) play(state, state.source);
            }
        };
        video.addEventListener('playing', state.onPlaying);
        video.addEventListener('error', state.onError);
    });
    controllers.set(hero, { disconnect });
    document.addEventListener('visibilitychange', refresh);
    if (states.length) {
        document.addEventListener('click', onInteraction, { capture: true });
        document.addEventListener('keydown', onInteraction, { capture: true });
    }
    mobile.addEventListener('change', refresh);
    tablet.addEventListener('change', refresh);
    reducedMotion.addEventListener('change', onMotionChange);
    track.addEventListener('zirbelino:slider-prepare', onPrepare);
    track.addEventListener('zirbelino:slider-change', onSlideChange);
    observer?.observe(hero);
    removalObserver?.observe(document.documentElement, { childList: true, subtree: true });
    visible = !observer;
    syncMotion();
}

export function initHeroVideos(root = document) {
    if (root.matches?.('.z-home-hero-slider')) initializeHeroVideos(root);
    root.querySelectorAll?.('.z-home-hero-slider').forEach(initializeHeroVideos);
}
